import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import MetaTags from "../../../helpers/Common/MetaTags";
import { getMetaConfig } from "../../../helpers/Common/metaConfig";
import { generateJobListingsSchema } from "../../../helpers/Common/jobPostingSchema";
import { useLocation, useSearchParams } from "react-router-dom";
import { Col, Container, Row, Spinner } from "reactstrap";
import { getJobs } from "../../../api/jobs";
import { CustomSnackbar } from "../../../helpers/CustomSnackbar";
import JobSearchOptions from "./JobSearchOptions";
import JobVacancyListing from "./JobVacancyListing";
import Section from "./Section";
import Sidebar from "./Sidebar";

const JobListing = () => {
  const jobsData = useSelector((state) => state.jobs);
  const jobs = Array.isArray(jobsData.data) ? jobsData.data : [];
  const [loading, setLoading] = useState(false);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const { pathname } = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  // ✅ Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const perPage = 20;

  // Sync current page with URL query parameter
  const pageFromUrl = parseInt(searchParams.get("page") || "1", 20);

  useEffect(() => {
    setCurrentPage(pageFromUrl);
  }, [pageFromUrl]);

  useEffect(() => {
    let query = searchParams.get("q");
    let location = searchParams.get("location");
    let category = searchParams.get("category");
    let JobTypeCategoryIds = searchParams.getAll("JobTypeCategoryIds");
    let DateFilter = searchParams.get("DateFilter");
    let keyword = searchParams.get("keyword");
    let gender = searchParams.get("gender");

    // ✅ Updated parameter extraction
    let SalaryRange = searchParams.get("SalaryRange");

    let params = [];

    params.push(`Page=${pageFromUrl}`);
    params.push(`PageSize=${perPage}`);

    if (query && query.trim()) {
      params.push(`Q=${encodeURIComponent(query)}`);
    }

    if (location && location.trim()) {
      params.push(`LocationName=${encodeURIComponent(location)}`);
    }

    if (category && category.trim()) {
      params.push(`CategoryId=${category}`);
    }

    if (JobTypeCategoryIds && JobTypeCategoryIds.length > 0) {
      const rawIds = JobTypeCategoryIds.flatMap((id) => id.split(","));
      const validIds = rawIds.map((id) => id.trim()).filter((id) => id !== "");

      validIds.forEach((id) => {
        params.push(`JobTypeCategoryIds=${encodeURIComponent(id)}`);
      });
    }

    // ✅ Work Experiences handling for multiple repeated query parameters
    let workExperiences = searchParams.get("workExperiences");
    if (workExperiences && workExperiences.trim()) {
      const selectedExperiences = workExperiences.split(",");
      selectedExperiences.forEach((exp) => {
        if (exp.trim()) {
          params.push(`workExperiences=${encodeURIComponent(exp.trim())}`);
        }
      });
    }

    if (DateFilter && DateFilter.trim()) {
      params.push(`DateFilter=${DateFilter}`);
    }

    if (keyword && keyword.trim()) {
      params.push(`JobsSearch[search_keywords]=${encodeURIComponent(keyword)}`);
    }

    // ✅ Pass `SalaryRange` directly to backend query string
    if (SalaryRange && SalaryRange.trim()) {
      const selectedRanges = SalaryRange.split(",");
      selectedRanges.forEach((range) => {
        params.push(`SalaryRange=${encodeURIComponent(range.trim())}`);
      });
    }

    if (gender && gender.trim()) {
      params.push(`Gender=${encodeURIComponent(gender)}`);
    }

    params.push("JobsSearch[is_active]=1");

    const newParams = `&${params.join("&")}`;

    if (pathname.includes("/jobs")) {
      setLoading(true);
      console.log("newParams::", newParams);
      getJobs(newParams)
        .then((res) => {
          console.log("getJobs res::", res);
          console.log("res?.data::", res?.data);
          console.log("res?.data?.data::", res?.data?.data);
          if (res?.data?.data) {
            console.log("totalCount sources::", {
              totalCount: res?.data?.totalCount,
              total: res?.data?.total,
              metaTotalCount: res?.data?._meta?.totalCount,
              dataLength: res?.data?.data?.length,
            });
            setFilteredJobs(res.data.data);
            setTotalCount(
              res?.data?.totalCount ||
              res?.data?.total ||
              res?.data?._meta?.totalCount ||
              res?.data?.data?.length ||
              0,
            );
          } else {
            console.log("no res?.data?.data found, resetting to empty");
            setFilteredJobs([]);
            setTotalCount(0);
          }
        })
        .catch((err) => {
          console.log("err::", err);
          console.log("err?.response?.data::", err?.response?.data);
          if (err?.response?.data?.message) {
            CustomSnackbar.error(err?.response?.data?.message);
          } else {
            CustomSnackbar.error(err.message);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [pathname, searchParams, pageFromUrl]);

  const totalPages = Math.ceil(totalCount / perPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || (totalPages > 0 && pageNumber > totalPages)) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", pageNumber);
    setSearchParams(newSearchParams);

    window.scrollTo({
      top: 100,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    window.scrollTo({
      top: 100,
      behavior: "smooth",
    });
  }, [jobsData]);

  const jobListingsData = generateJobListingsSchema();

  const jobsMeta = getMetaConfig("jobs", {
    jobPosting: jobListingsData,
  });

  return (
    <React.Fragment>
      <MetaTags {...jobsMeta} />
      <Section />
      <section className="section">
        <Container>
          <Row>
            <Col lg={9}>
              <div className="me-lg-5">
                <JobSearchOptions />

                {loading || jobsData?.loading ? (
                  <div style={{ textAlign: "center", marginTop: 110 }}>
                    <Spinner size="lg"></Spinner>
                    <p>Loading...</p>
                  </div>
                ) : (
                  <JobVacancyListing
                    jobs={
                      pathname?.includes("/job/lists") ? jobs : filteredJobs
                    }
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            </Col>
            <Sidebar />
          </Row>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default JobListing;
